import {useEffect,forwardRef,useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  TextInput, 
  Select, 
  Text, 
  Radio, 
  Group,
  Stack,
  Space
} from '@mantine/core';
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { Row, Col } from 'react-bootstrap';
import { SaveResponses } from "../actions/ResponsesActions";
import { GetPaises,GetTipoDocumento ,GetTipoDocumentoEmpresa} from "../actions/FormDataActions"
import { stepIds} from "../enums/fieldsDataConfig";
import { useStepResponses } from "../hooks/useStepResponses";
import useFormTracking from "../hooks/useFormTracking";


const FacturacionDataForm = forwardRef(({ stepNumber, id,queryParams }, ref) => {
  const dispatch = useDispatch();
  
  const response = useSelector((state) => state.respuestas.steps[stepNumber]?.responses || {})
  const personalResponse = useStepResponses(stepIds.PERSONA);
  const contactResponse = useStepResponses(stepIds.CONTACTO);
  const { data: dataPaises} = useSelector((state) => state.datosFormulario.paises);
  const { data: dataDocumentos } = useSelector((state) => state.datosFormulario.tiposDoc);
  const { data: dataDocumentosEmpresa } = useSelector((state) => state.datosFormulario.tiposDocEmpresa);
  const { data: dataProvincias } = useSelector((state) => state.datosFormulario.provincias);

  const { checkoutSummary: summary, steps: stepResponsesRedux } = useSelector((state) => state.respuestas);
  const knownUser = useSelector((state) => state.respuestas.knownUser);
  const { track } = useFormTracking(stepNumber);
  const trackSelect = (fieldName) => (value) => {
    form.setFieldValue(fieldName, value);
    if (value) track('field_completed', { fieldName });
  };


  const getValidations = () => {
    const validations = {
      tipoFacturacion: (value) => (value ? null : "Seleccione un tipo de facturación"),
      tipoDocumento: (value) => (value ? null : "Seleccione un tipo de documento"),
      numeroDocumento: (value, values) => {
        if (values.tipoDocumento === 'PAS') {
          return /^[A-Za-z0-9]{1,14}$/.test(value) ? null : "El campo debe tener entre 1 y 14 caracteres";
        }
        // Permitir CUIL/CUIT (8-14) tanto para empresa como para "otraPersona" cuando el tipo documento lo indique
        if (values.tipoDocumento === 'CUIL' || values.tipoDocumento === 'CUIT' || values.tipoFacturacion === 'empresa') {
          return /^\d{8,14}$/.test(value) ? null : "Ingrese entre 8 y 14 dígitos";
        }
        return /^\d{7,8}$/.test(value) ? null : "El campo debe tener 7 a 8 dígitos";
      },
      email: (value) => (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value) ? null : "El email no es válido"),
      telefono: (value) => (/^\d{10,14}$/.test(value) ? null : "Ingrese un teléfono válido (10-14 dígitos)"),
      calle: (value) => (/^[A-Za-zÁ-Üá-üÑñ0-9\s]{3,60}$/.test(value || '') ? null : "Sólo letras y números (mín. 3)"),
      altura: (value) => (/^\d+$/.test(value) ? null : "Ingrese una altura válida"),
      codigoPostal: (value) => (/^\d+$/.test(value) ? null : "Ingrese un código postal válido"),
      localidad: (value) => (/^[A-Za-zÁ-Üá-üÑñ\s]{3,60}$/.test(value || '') ? null : "Sólo letras (mín. 3)"),
      paisResidencia: (value) => (value ? null : "Seleccione un país"),
      provincia: (value) => (value ? null : "Seleccione una provincia"),
    };

    if (response.tipoFacturacion === 'empresa') {
      validations.razonSocialEmpresa = (value) => (/^[A-Za-zÁ-Üá-üÑñ\s]{3,60}$/.test(value || '') ? null : "Sólo letras (mín. 3)");
      validations.descripcionEmpresa = (value) => (/^[A-Za-zÁ-Üá-üÑñ\s]{3,60}$/.test(value || '') ? null : "Sólo letras (mín. 3)");
    } else {
      validations.apellido = (value) => (/^[A-Za-zÁ-Üá-üÑñ\s]{2,60}$/.test(value || '') ? null : "Sólo letras (mín. 2)");
      validations.nombre = (value) => (/^[A-Za-zÁ-Üá-üÑñ\s]{2,60}$/.test(value || '') ? null : "Sólo letras (mín. 2)");
    }
    return validations;
  };

  const form = useForm({
    initialValues: {
      tipoFacturacion: response.tipoFacturacion || null,
      tipoDocumento: response.tipoDocumento || null,
      numeroDocumento: response.numeroDocumento || "",
      apellido: response.apellido || "",
      nombre: response.nombre || "",
      razonSocialEmpresa: response.razonSocialEmpresa || "",
      descripcionEmpresa: response.descripcionEmpresa || "",
      email: response.email || "",
      telefono: response.telefono || "",
      calle: response.calle || "",
      altura: response.altura || "",
      piso: response.piso || "",
      departamento: response.departamento || "",
      codigoPostal: response.codigoPostal || "",
      localidad: response.localidad || "",
      paisResidencia: response.paisResidencia || null,
      provincia: response.provincia || null,
      provinciaDescripcion: response.provinciaDescripcion || '',
    },
    validate: getValidations(),
  })

  const [debouncedValues] = useDebouncedValue(form.values, 300);
      
  useEffect(() => {
    dispatch(SaveResponses(id, debouncedValues));
  }, [debouncedValues, dispatch, id]);

  useEffect(() => {
    if(response.tipoFacturacion){
      handleSetStudentResponses(response.tipoFacturacion);
    }
  }, [response.tipoFacturacion])

  useEffect(() => {
    dispatch(GetPaises())
    dispatch(GetTipoDocumento())
    dispatch(GetTipoDocumentoEmpresa())
  }, [dispatch])


  /*useEffect(() => {
    if (queryParams?.t === "m" && valorCarreraComunityUAI?.[0]) {
      dispatch(SaveResponses(id, { ...form.values, valorMatricula: valorCarreraComunityUAI[0].f_matric_con_desc }));
    } else {
      dispatch(SaveResponses(id, { ...form.values, valorMatricula: bonus.bonus.precioMatricula }));
    }
  }, []);*/

  useEffect(() => {
    handleSetPrecioMatricula();
  }, [summary]);

  const handleSetPrecioMatricula = () => {
    let precioFinal = null;
    
    // Si es usuario conocido (tiene token), usar precioComunidad
    if (knownUser.comunity && summary.precioComunidad && summary.precioComunidad > 0) {
      precioFinal = summary.precioComunidad;
    } 
    // Si no es usuario conocido, usar precioDescuento
    else if (summary.precioDescuento && summary.precioDescuento > 0) {
      precioFinal = summary.precioDescuento;
    } 
    // Fallback al precio original
    else if (summary.precioOriginal) {
      precioFinal = summary.precioOriginal;
    }

    dispatch(SaveResponses(id, { ...form.values, valorMatricula: precioFinal }));
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }))

  const handleSavePais = (pais) => {
    form.setValues({['paisResidencia']: pais});
    form.setValues({['provincia']: null});
    form.setValues({['provinciaDescripcion']: null});
    if (pais) track('field_completed', { fieldName: 'paisResidencia' });
  };

  const handleSaveResponseProvincia = (provincia) => {
    let prov = dataProvincias.find((p) => p.value === provincia);
    if (prov) {
      form.setValues({['provincia']: prov.value});
      form.setValues({['provinciaDescripcion']: prov.label});
    } else {
      form.setValues({['provincia']: 'OTR'});
      form.setValues({['provinciaDescripcion']: provincia});
    }
    if (provincia) track('field_completed', { fieldName: 'provincia' });
  };

  const handleSetStudentResponses = (tipo) => {
    const isAlumno = tipo === 'alumno';

    form.setValues({
      tipoDocumento: isAlumno ? personalResponse.documentoTipo : null,
      numeroDocumento: isAlumno ? personalResponse.documentoNumero : "",
      apellido: isAlumno ? personalResponse.apellidos : "",
      nombre: isAlumno ? personalResponse.nombres : "",
      email: isAlumno ? contactResponse.email : "",
      telefono: isAlumno ? contactResponse.telefonoCelular : "",
      calle: isAlumno ? contactResponse.calle : "",
      altura: isAlumno ? contactResponse.altura : "",
      piso: isAlumno ? contactResponse.piso : "",
      departamento: isAlumno ? contactResponse.departamento : "",
      codigoPostal: isAlumno ? contactResponse.codPostal : "",
      localidad: isAlumno ? contactResponse.localidad : "",
      paisResidencia: isAlumno ? contactResponse.paisResidencia : null,
      provincia: isAlumno ? contactResponse.provincia : null,
      provinciaDescripcion: isAlumno ? contactResponse.provinciaDescripcion : null,
    });
  };

  return (
    <Container size="100%" style={{ minHeight: '100%', overflow: 'visible' }}>
      <Stack spacing="md">
        <div>
          <Text size="sm" fw={500} mb={5}>Deseo facturar a nombre de</Text>
          <Radio.Group
            {...form.getInputProps("tipoFacturacion")}
            onChange={(value) => {
              form.setFieldValue('tipoFacturacion', value);
              if (value) track('field_completed', { fieldName: 'tipoFacturacion' });
            }}
            name="billingType"
          >
            <Group>
              <Radio value="alumno" label="Alumno" color="pink" />
              <Radio value="otraPersona" label="Otra persona" color="pink" />
              <Radio value="empresa" label="Empresa" color="pink" />
              <Space h="md" /> 
            </Group>
          </Radio.Group>
        </div>

        <Row>
          <Col md={6}>
            <Select
              required
              label="Tipo de documento"
              placeholder="Seleccione tipo de documento"
              data= {form.values.tipoFacturacion==='empresa'? dataDocumentosEmpresa: dataDocumentos}
              {...form.getInputProps("tipoDocumento")}
              onChange={trackSelect('tipoDocumento')}
            />
          </Col>
          <Col md={6}>
            <TextInput
              required
              label="Número"
              type="text"
              onKeyDown={(event) => {
                if (form.values.tipoDocumento === 'PAS') {
                  // Solo permite letras y números para PAS
                  if (!/^[a-zA-Z0-9]$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                } else {
                  // Si no es PAS, solo permite números
                  if (!/^\d$/.test(event.key) && event.key !== "Backspace" && event.key !== "Tab") {
                    event.preventDefault();
                  }
                }
              }}
              onPaste={e => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('Text').replace(/[^a-zA-Z0-9]/g, '');
                form.setFieldValue('numeroDocumento', pasted);
              }}
              placeholder="Ej: 11 111 111"
              {...form.getInputProps("numeroDocumento")}
            />
          </Col>
        </Row>
        { form.values?.tipoFacturacion !== 'empresa' ? (
          <Row>
            <Col md={6}>
              <TextInput
                required
                label="Apellido"
                placeholder="Ingrese apellido"
                onPaste={(e) => e.preventDefault()} 
                {...form.getInputProps("apellido")}
                //onChange={(event) => handleSaveResponse("apellido", event.currentTarget.value)}
              />
            </Col>
            <Col md={6}>
              <TextInput
                required
                label="Nombre"
                placeholder="Ingrese nombre"
                onPaste={(e) => e.preventDefault()} 
                {...form.getInputProps("nombre")}
                //onChange={(event) => handleSaveResponse("nombre", event.currentTarget.value)}
              />
            </Col>
          </Row>
        ):(
          <Row>
            <Col md={6}>
              <TextInput
                required
                label="Razón Social"
                onPaste={(e) => e.preventDefault()} 
                placeholder="Ingrese la Razón Social de la empresa"
                {...form.getInputProps("razonSocialEmpresa")}
                //onChange={(event) => handleSaveResponse("razonSocialEmpresa", event.currentTarget.value)}
              />
            </Col>
            <Col md={6}>
              <TextInput
                required
                label="Descripción"
                onPaste={(e) => e.preventDefault()} 
                placeholder="Ingrese una descripción de la empresa"
                {...form.getInputProps("descripcionEmpresa")}
                //onChange={(event) => handleSaveResponse("descripcionEmpresa", event.currentTarget.value)}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={6}>
            <TextInput
              required
              label="Email"
              type="email"
              onPaste={(e) => e.preventDefault()} 
              placeholder="Ingrese email"
              {...form.getInputProps("email")}
              //onChange={(event) => handleSaveResponse("email", event.currentTarget.value)}
            />
          </Col>
          <Col md={6}>
            <TextInput
              required
              type="number"
              label="Teléfono"
              onPaste={(e) => e.preventDefault()} 
              placeholder="Ingrese teléfono"
              {...form.getInputProps("telefono")}
              //onChange={(event) => handleSaveResponse("telefono", event.currentTarget.value)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Select
              required
              label="País de residencia"
              placeholder="Seleccione país"
              data={dataPaises}
              {...form.getInputProps("paisResidencia", {
                onChange: (value) => {
                  handleSavePais(value);
                },
              })}
            />
          </Col>
          <Col md={3}>
          {form.values.paisResidencia==='AR' ? (
            <Select
              required
              label="Provincia"
              placeholder="Seleccione provincia"
              data={dataProvincias}
              {...form.getInputProps("provincia", {
                onChange: (value) => {
                  handleSaveResponseProvincia(value);
                },
              })}
            />
          ):(
            <TextInput
            required
            label="Provincia"
            placeholder="Ingrese la provincia"
            {...form.getInputProps("provinciaDescripcion", {
              onChange: (e) => {
                handleSaveResponseProvincia(e.target.value);
              },
            })}
          />
        )}
          </Col>
          <Col md={3}>
            <TextInput
              required
              label="Localidad"
              placeholder="Ingrese localidad"
              onPaste={(e) => e.preventDefault()} 
              {...form.getInputProps("localidad")}
              //onChange={(event) => handleSaveResponse("localidad", event.currentTarget.value)}
            />
          </Col>
          <Col md={3}>
            <TextInput
              required
              label="Código Postal"
              onPaste={(e) => e.preventDefault()} 
              placeholder="Ingrese código postal"
              {...form.getInputProps("codigoPostal")}
              //onChange={(event) => handleSaveResponse("codigoPostal", event.currentTarget.value)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <TextInput
              required
              label="Calle"
              onPaste={(e) => e.preventDefault()} 
              placeholder="Ingrese calle"
              {...form.getInputProps("calle")}
              //onChange={(event) => handleSaveResponse("calle", event.currentTarget.value)}
            />
          </Col>
          <Col md={3}>
            <TextInput
              required
              type="number"
              label="Altura"
              onPaste={(e) => e.preventDefault()} 
              placeholder="Ingrese altura"
              {...form.getInputProps("altura")}
              //onChange={(event) => handleSaveResponse("altura", event.currentTarget.value)}
            />
          </Col>
          <Col md={3}>
            <TextInput
              label="Piso"
              type="number"
              placeholder="Ingrese piso"
              onPaste={(e) => e.preventDefault()} 
              {...form.getInputProps("piso")}
              //onChange={(event) => handleSaveResponse("piso", event.currentTarget.value)}
            />
          </Col>
          <Col md={3}>
            <TextInput
              label="Departamento"
              onPaste={(e) => e.preventDefault()} 
              placeholder="Ingrese departamento"
              {...form.getInputProps("departamento")}
              onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ0-9\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
            />
          </Col>
        </Row>
      </Stack>
    </Container>
  );
});

export default FacturacionDataForm;